import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { FiltersAutocomplete } from '../shared/FiltersAutocomplete.jsx';
import { GetLookupDataRc, addCrisisMaterials, fetchRiskCrisis, getMaterialsCount } from './RiskCrisis';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { formatErrorMessage } from '../section_sf/NoBcp';
import { BasePopup, SuccessPopup } from '../shared/popups/BasePopup.jsx';
import { StyledCircularProgress } from '../shared/_snippets.jsx';
import { _theme } from '../shared/_theme';
import { ThemeProvider } from '@mui/material/styles';
import {
    DataGridPro,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridToolbar,
    GridToolbarContainer, GridToolbarQuickFilter,
    useGridApiRef
} from '@mui/x-data-grid-pro';
import { getRowId, materialsDataColumns } from './_materialsTable.jsx';
import { WarningAmber } from '@mui/icons-material';
import { RCFilterType, formatFilterOptionsForAPI } from '../shared/_filters';
import RCFiltersAutocomplete from './RCFiltersAutocomplete.jsx';

/**
 * @param {Object} crisisDetails
 * @param {function(string)} throwError
 * @param {function()} onClose
 * @param {function(number)} handleUpdate(addedRowsCount)
 * @return {JSX.Element}
 * @constructor
 */
export const AddMaterialsPopup = ({ crisisDetails, throwError, onClose, handleUpdate, context }) => {
    const [showFilters, setShowFilters] = useState(true);
    const [showMaterials, setShowMaterials] = useState(false);
    const [materialsFetchError, setMaterialsFetchError] = useState('');
    const [showSelectAllWarning, setShowSelectAllWarning] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = React.useState(false);
    const [selectedFilterOptions, setSelectedFilterOptions] = useState({});
    const [selectedAllInFilter, setSelectedAllInFilter] = useState({});
    const [standardFilters, setStandardFilters] = useState({});
    const [topFilters] = useState({});
    const [pageShown, setPageShown] = useState(0);
    const [materialsSelection, setMaterialsSelection] = useState([]);
    const [materialsToAddCount, setMaterialsToAddCount] = useState(0);

    const handleClose = () => {
        setShowFilters(false);
        setShowMaterials(false);
        setShowSuccess(false);
        onClose();
    };

    const handleShowFilters = () => {
        setShowFilters(true);
        setShowMaterials(false);
        setShowSuccess(false);
    };

    const handleShowMaterials = (resetPage = true) => {
        if (resetPage) {
            setPageShown(0);
        }
        setMaterialsSelection([]);
        setMaterialsToAddCount(0);
        setShowFilters(false);
        setShowMaterials(true);
        setShowSelectAllWarning(false);
        setShowSuccess(false);
    };

    const handleAddMaterials = async () => {
        setShowFilters(false);
        setShowMaterials(false);
        setLoading(true);
        const response = await addCrisisMaterials(crisisDetails, materialsSelection);
        setLoading(false);
        if (response.statusCode) {
            throwError(null, formatErrorMessage(response, 'An error occurred while adding entries'));
            handleClose();
        } else if (response['Records_Inserted'] === 0) {
            handleShowMaterials(false);
            throwError(null, 'All the materials selected were already added to the crisis. No changes to the crisis were made.');
        } else {
            setMaterialsToAddCount(response['Records_Inserted']);
            setShowSuccess(true);
        }
    };

    if (loading) {
        return <BasePopup
            content={<StyledCircularProgress />}
            onClose={handleClose}
        />;
    } else if (showFilters) {
        const filtersState = {
            // compact way to forward state to the children components
            selectedFilterOptions, setSelectedFilterOptions,
            selectedAllInFilter, setSelectedAllInFilter,
            standardFilters, setStandardFilters,
        };
        return <BasePopup
            title='Please, enter the filters to find the material'
            dialogProps={{
                maxWidth: 'xl',
            }}
            content={
                <div className='add-material-popup-filters-container'>
                    <div className='add-material-popup-filters-column-container'>
                        <FilterComponent type={RCFilterType.ZONE} {...filtersState} />
                        <FilterComponent type={RCFilterType.MARKET} {...filtersState} />
                        <FilterComponent type={RCFilterType.PLANT} {...filtersState} />
                        <FilterComponent type={RCFilterType.MATERIAL} {...filtersState} />
                        <FilterComponent type={RCFilterType.SPEND_CAT_L1} {...filtersState} />
                    </div>
                    <div className='add-material-popup-filters-column-container'>
                        <FilterComponent type={RCFilterType.SPEND_CAT_L2} {...filtersState} />
                        <FilterComponent type={RCFilterType.SPEND_CAT_L3} {...filtersState} />
                        <FilterComponent type={RCFilterType.SPECIFICATION} {...filtersState} />
                        <FilterComponent type={RCFilterType.MAIN_VENDOR} {...filtersState} />
                        <FilterComponent type={RCFilterType.VENDOR_SITE} {...filtersState} />
                    </div>
                    <div className='add-material-popup-filters-column-container'>
                        <FilterComponent type={RCFilterType.VENDOR_SITE_CITY} {...filtersState} />
                        <FilterComponent type={RCFilterType.VENDOR_SITE_COUNTRY} {...filtersState} />
                        <FilterComponent type={RCFilterType.FINANCIAL_RATING} {...filtersState} />
                        <FilterComponent type={RCFilterType.BCP_STATUS} {...filtersState} />
                    </div>
                </div>
            }
            buttons={
                <Button
                    variant='contained'
                    className='add-material-popup-button'
                    disabled={Object.keys(standardFilters).length === 0}
                    onClick={handleShowMaterials}
                    endIcon={<ArrowForwardIcon />}
                >
                    Apply
                </Button>
            }
            onClose={handleClose}
        />;
    } else if (showMaterials) {
        let content;
        if (materialsFetchError.length === 0) {
            content = <div className='add-material-popup-datagrid-container'>
                <AddMaterialsTable
                    standardFilters={standardFilters}
                    topFilters={topFilters}
                    pageShown={pageShown}
                    setLoading={setLoading}
                    throwError={(e, message) => { setMaterialsFetchError(message) }}
                    onSelectionChange={(selection, allSelectedAndMoreAvailable) => {
                        setMaterialsSelection(selection);
                        setMaterialsToAddCount(selection.length);
                        setShowSelectAllWarning(allSelectedAndMoreAvailable);
                    }}
                    onPageChange={setPageShown}
                    context={context}
                />
                {showSelectAllWarning && <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', right: '70px', bottom: '100px' }}>
                    <WarningAmber color='primary' fontSize='large' sx={{ fontSize: '40px !important', marginRight: '10px' }} />
                    <Typography color='primary'>
                        The select all button is only selecting entries on the current page. <br />
                        To select all entries based on the set filters, please ensure to repeat the add material steps on each available page.
                    </Typography>
                </div>}
            </div>;
        } else {
            content = <div className='add-material-popup-datagrid-container add-material-popup-success-container'>
                <Typography style={{ fontStyle: 'italic' }}>{materialsFetchError}</Typography>
            </div>;
        }
        return <BasePopup
            content={content}
            dialogProps={{
                maxWidth: 'xl',
                fullWidth: true,
            }}
            buttons={<>
                <Button
                    variant='text'
                    className='add-material-popup-button'
                    onClick={handleShowFilters}
                    startIcon={<ArrowBackIcon />}
                >
                    See and edit filters
                </Button>
                <div style={{ flexGrow: 2 }} />
                <Button
                    variant='contained'
                    disabled={materialsToAddCount === 0}
                    className='add-material-popup-button'
                    onClick={handleAddMaterials}
                >
                    {`Add ${materialsToAddCount} entries`}
                </Button>
            </>}
            onClose={handleClose}
        />;
    } else if (showSuccess) {
        return <SuccessPopup
            text={`${materialsToAddCount} entries were added`}
            onClose={() => {
                handleUpdate(materialsToAddCount);
                handleShowMaterials(false); // give opportunity to user to add materials from other pages
            }}
        />;
    }

    return null;
};

/**
 * Customized filter for AddMaterial popup
 * @param {RCFilterType} type
 * @param {Object} selectedFilterOptions
 * @param {function(Object)} setSelectedFilterOptions
 * @param {Object} selectedAllInFilter
 * @param {function(Object)} setSelectedAllInFilter
 * @param {Object} standardFilters
 * @param {function(Object)} setStandardFilters
 * @constructor
 */
const FilterComponent = ({ type, selectedFilterOptions, setSelectedFilterOptions, selectedAllInFilter, setSelectedAllInFilter, standardFilters, setStandardFilters }) => {
    const fetchOptions = async (searchValue) => {
        const response = await GetLookupDataRc(
            searchValue,
            type.key,
            standardFilters,
            {},
        );
        return response.slice(0, 100000);
    };

   // Add limitcheck property to set a input text value 
    return <FiltersAutocomplete
        label={type.label}
        limitTags={2}
        fetchOptions={fetchOptions}
        getOptionLabel={RCFiltersAutocomplete.getOptionLabel}
        onChange={(selectedValues, selectedAll) => {
            const apiValues = formatFilterOptionsForAPI(type.key, selectedValues);
            if (apiValues.length > 0) {
                selectedFilterOptions[type.key] = selectedValues;
                if (selectedAll) {
                    selectedAllInFilter[type.key] = true;
                } else {
                    delete selectedAllInFilter[type.key];
                }
                standardFilters[type.key] = apiValues;
            } else {
                delete selectedFilterOptions[type.key];
                delete selectedAllInFilter[type.key];
                delete standardFilters[type.key];
            }
            setSelectedFilterOptions(selectedFilterOptions);
            setSelectedAllInFilter(selectedAllInFilter);
            setStandardFilters({ ...standardFilters });
        }}
        style={{ width: '200px', marginBottom: '20px' }}
        selectedOptions={selectedFilterOptions[type.key]}
        showSelectAll={selectedAllInFilter[type.key]}
        limitCheck={0}
    />;
};

/**
 *
 * @param {Object} props
 * @param {Object} props.standardFilters
 * @param {Object} props.topFilters
 * @param {number} props.pageShown
 * @param {function(Array, boolean)} props.onSelectionChange(selection, allSelectedAndMoreAvailable)
 * @param {function(number)} props.onPageChange(page)
 * @param {function(Event, string)} props.throwError
 * @return {JSX.Element}
 * @constructor
 */
const AddMaterialsTable = ({ standardFilters, topFilters, pageShown, throwError, onSelectionChange, onPageChange, context }) => {
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const pageSize = 500;
    const apiRef = useGridApiRef();

    const fetchMaterials = async (stdFilters, topFilters, page, fetchCount) => {
        setLoading(true);
        const data = await fetchRiskCrisis(stdFilters, topFilters, page * pageSize);

        if (data.error) {
            console.error(data.message);
            throwError(null, data.message);
        } else {
            const newRows = data["materials"].map(function (row) {
                return {
                    ...row,
                    id: getRowId(row),
                };
            });

            if (fetchCount) {
                if (data.moreRecordsAvailable) {
                    const countData = await getMaterialsCount(stdFilters, topFilters);
                    setRowCount(countData.counts);
                } else {
                    setRowCount(newRows.length);
                }
            }
            setRows(newRows);
            setLoading(false);
        }
    };

    // data loading after the filters have been applied
    useEffect(() => {
        fetchMaterials(standardFilters, topFilters, pageShown, true);
    }, [standardFilters, topFilters]);

    const handlePageChange = (newPage, details) => {
        onPageChange(newPage);
        fetchMaterials(standardFilters, topFilters, newPage);
    };

    // Datagrid UI customization
    const columnsMaterial = materialsDataColumns(context);

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer style={{ justifyContent: 'flex-end' }}>
                <GridToolbar />
                <GridToolbarQuickFilter />
            </GridToolbarContainer>
        );
    };

    return <ThemeProvider theme={_theme}>
        <div className={"rc-tables"} id={"riskAndCrisisTables"}>
            <div style={{ display: "flex", width: "100%" }}>
                <div className={"rc-data-table"}>
                    <div
                        className={"rc-data-table-scroller"}
                    >
                        <DataGridPro
                            rows={rows}
                            loading={loading}
                            classes={{
                                virtualScroller: 'with-scrollbar',
                            }}
                            columns={columnsMaterial}
                            checkboxSelection
                            initialState={{
                                pinnedColumns: {
                                    left: [
                                        GRID_CHECKBOX_SELECTION_COL_DEF.field,
                                    ],
                                },
                            }}
                            onSelectionModelChange={(newSelection) => {
                                setSelectionModel(newSelection);
                                const selection = Array.from(apiRef.current.getSelectedRows().values());
                                const allSelectedAndMoreAvailable = selection.length === rows.length && rowCount > pageSize;
                                onSelectionChange(selection, allSelectedAndMoreAvailable);
                            }}
                            components={{
                                Toolbar: CustomToolbar,
                            }}
                            disableDensitySelector={true}
                            disableColumnSelector={true}
                            disableColumnFilter={true}
                            apiRef={apiRef}
                            pagination={true}
                            paginationMode="server"
                            rowCount={rowCount}
                            page={pageShown}
                            pageSize={pageSize}
                            rowsPerPageOptions={[pageSize]}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    </ThemeProvider>;
}
